import { useTranslation } from 'react-i18next';
import CatalogListHeader from '../../components/CatalogListHeader/CatalogListHeader';
import LoaderOverlay from '../../components/LoaderOverlay/LoaderOverlay';
import { ActionIcon, Button, Card, Flex, Grid, Text, TextInput, Tooltip } from '@mantine/core';
import { useHistory, useLocation } from 'react-router-dom';
import { IconColumns, IconFileSpreadsheet, IconLayoutGrid } from '@tabler/icons-react';
import CatalogContainer from '../../components/CatalogContainer/CatalogContainer';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import debounce from 'lodash/debounce';
import useDownloadExcel from '../../hooks/useDownloadExcel/useDownloadExcel';
import { formatMoneyString } from '../../utils/strings';
import { useFetchOrderBySequence, useGetOrders, useGetOrdersHeaderSummary } from '../../hooks/orders/queries';
import { IOrder } from '../../interfaces/Order';
import OrderStatuses from '../../enum/OrderStatuses';
import { useDisclosure } from '@mantine/hooks';
import OrderProductsModal from '../../components/OrderModals/OrderProductsModal';
import OrderPaymentLinesModal from '../../components/OrderModals/OrderPaymentLinesModal';
import { showNotification } from '../../utils/Notification/Notification';
import {
  useCreateOrderEvidence,
  useSendEmailReceipt,
  useSetOrderCancelled,
  useSetOrderCleaning,
  useSetOrderDelivered
} from '../../hooks/orders/mutations';
import { exportOrdersListV2 } from '../../services/order.service';
import { downloadTicketAsPNG, tryPrintTicket } from '../../utils/ticketHelper';
import { useSelector } from 'react-redux';
import { modals } from '@mantine/modals';
import UpdateOrderForm from '../../components/OrderModals/UpdateOrderForm';
import { MRT_PaginationState } from 'mantine-react-table';
import OrdersCardView from '../../components/PickupAndDeliveryViews/OrdersCardView';
import OrdersTableView from '../../components/PickupAndDeliveryViews/OrdersTableView';
import OrderEvidencesCarouselModal from '../../components/OrderModals/OrderEvidencesCarouselModal';
import UploadOrderEvidenceFile from '../NewOrder/components/UploadOrderEvidenceFile';
import { useGetStoreById } from '../../hooks/stores/queries';
import { buildOrderFolio } from '../../utils/ordersUtils';
import { useOrderPaymentLinesModal } from '../../hooks/useOrderPaymentLinesModal';
import { createWebCompletionTicketAutoPrinter } from '../../utils/orderCompletionTicket';
import { useRequestCashierPinValidation } from '../../hooks/useRequestCashierPinValidation';
import { getStoreByIdV2 } from '../../services/store.service';
import { IStore } from '../../interfaces/Store';

const PickupAndDeliveryOrders = () => {
  const { t } = useTranslation([
    'FieldName',
    'CommonButtons',
    'Notifications',
    'OrdersPage',
    'PickupAndDeliveryPage',
    'UpdateOrderForm',
    'ReportsPage',
    'RoutesPage',
    'OrdersHeaderSummary',
    'Backend'
  ]);
  const { company, user } = useSelector((state: any) => state.user);
  const hideOrderTotals = user?.permission['hideOrderTotals']?.canView;
  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const searchParamStoreId = searchParams.get('storeId') || '';
  const searchTermInputRef = useRef<HTMLInputElement>(null);
  const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);
  const orderStatusFilter = `[${OrderStatuses.PICKUP},${OrderStatuses.PICKINGUP},${OrderStatuses.PENDING_STORE_RECEPTION_VALIDATION},${OrderStatuses.DELIVERY},${OrderStatuses.DELIVERING}]`;
  const [showTableView, setShowTableView] = useState(false);

  const setSearchParamsDebounced = debounce((val: string) => {
    setSearchTerm(val || undefined);
  }, 500);
  const queryClient = useQueryClient();
  const { downloadExcelV2, loadingExport } = useDownloadExcel();
  const [openedOrderProductsModal, orderProductsModalhandlers] = useDisclosure(false);
  const [openedOrderEvidenceModal, evidenceModalhandlers] = useDisclosure(false);
  const [selectedOrder, setSelectedOrder] = useState<IOrder | null>(null);
  const sendEmailReceipt = useSendEmailReceipt();
  const setOrderCleaning = useSetOrderCleaning();
  const setOrderDelivered = useSetOrderDelivered();
  const { requestPin } = useRequestCashierPinValidation();
  const delivering = useRef<AbortController | null>(null);
  useEffect(() => () => {
    delivering.current?.abort();
    delivering.current = null;
  }, [searchParamStoreId]);
  const setOrderCancelled = useSetOrderCancelled();
  const createOrderEvidence = useCreateOrderEvidence();
  const { data: store } = useGetStoreById(!!searchParamStoreId, searchParamStoreId);
  const fetchOrderBySequence = useFetchOrderBySequence();
  const storeReceptionValidationEnabled = store?.storeReceptionValidationConfig?.enabled === true;
  const paymentLinesModal = useOrderPaymentLinesModal({ store });
  const completionTicketAutoPrint = useMemo(
    () => createWebCompletionTicketAutoPrinter({
      fetchOrderBySequence,
      t,
      companyLogoUrl: company.logoUrl,
      companyTaxInfo: company?.companyTaxInfo
    }),
    [company.logoUrl, company?.companyTaxInfo, fetchOrderBySequence, t]
  );
  const limitPerReq = 9;
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 9
  });
  const {
    data,
    fetchNextPage,
    hasNextPage,
    refetch: refetchList,
    isLoading,
    isError
  } = useGetOrders(!!searchParamStoreId, {
    pageNum: showTableView ? pagination.pageIndex : undefined,
    q: searchTerm,
    status: orderStatusFilter,
    storeId: searchParamStoreId,
    limit: limitPerReq,
    fetchSection: 'true'
  });

  const { data: summaryData } = useGetOrdersHeaderSummary({
    status: orderStatusFilter,
    store: searchParamStoreId
  });

  const orders = useMemo<IOrder[]>(() => {
    return (data?.pages || []).reduce((list: IOrder[], { orders }: { orders: IOrder[] }) => {
      list.push(...orders);
      return list;
    }, [] as IOrder[]);
  }, [data]);

  const totalRowsCount = useMemo(() => data?.pages[0]?.totalRows[0]?.totalRowsCount || -1, [data]);
  const pages = useMemo(() => Math.ceil(totalRowsCount / 9) || -1, [totalRowsCount]);

  const showOrderProductsModal = (order: IOrder) => {
    setSelectedOrder(order);
    orderProductsModalhandlers.open();
  };

  const showPaymentLinesModal = async (order: IOrder) => {
    setSelectedOrder(order);
    await paymentLinesModal.open(order);
  };
  const onAddPaymentLine = async () => {
    try {
      await refetchList();
    } catch (e) {}
  };

  const onDeletePaymentLine = async () => {
    try {
      await refetchList();
    } catch (e) {
      showNotification({ type: 'Error', title: t('Notifications:somethingWrong') });
    }
  };

  const onUpdatePaymentLine = async () => {
    try {
      await refetchList();
    } catch (e) {
      showNotification({ type: 'Error', title: t('Notifications:somethingWrong') });
    }
  };

  const onSendEmailReceipt = async (orderId: string) => {
    try {
      if (sendEmailReceipt.isPending) return;
      await sendEmailReceipt.mutateAsync(orderId);
      showNotification({ type: 'Default', title: t('Notifications:success') });
    } catch (e) {
      showNotification({ type: 'Error', title: t('Notifications:somethingWrong') });
    }
  };

  const clearSearchTerm = () => {
    if (searchTermInputRef.current) {
      searchTermInputRef.current.value = ''; // Reset the input field value via ref
      setSearchParamsDebounced(undefined); // Reset the state to trigger re-render
    }
  };

  const onPrintTicket = async (order: IOrder) => {
    try {
      tryPrintTicket(order, order.createdIn, t, 'ticketStructure', company.logoUrl, false, company?.companyTaxInfo);
    } catch (e) {}
  };

  const onDownloadPNGTicket = async (order: IOrder) => {
    try {
      downloadTicketAsPNG(order, order.createdIn, t, 'ticketStructure', company.logoUrl);
    } catch (e) {
      console.log('[ERROR] onDownloadPNGTicket:', e);
    }
  };

  const showOrderEvidenceModal = (order: IOrder) => {
    setSelectedOrder(order);
    evidenceModalhandlers.open();
  };

  const handleEvidenceDeleted = (evidenceId: string) => {
    setSelectedOrder((prev) =>
      prev
        ? {
            ...prev,
            evidence: prev.evidence?.filter((evidence) => evidence._id !== evidenceId) ?? []
          }
        : prev
    );
  };

  const onCreateOrderEvidence = async (orderId: string, data: FormData) => {
    try {
      if (createOrderEvidence.isPending) return;
      await createOrderEvidence.mutateAsync({ orderId, data });
      showNotification({ type: 'Default', title: t('Notifications:success') });
      modals.close('uploadEvidenceModal');
      await refetchList();
      return true;
    } catch (e) {
      showNotification({ type: 'Error', title: t('Notifications:somethingWrong') });
      return false;
    }
  };

  const onShowCreateOrderEvidenceModal = (order: IOrder) => {
    modals.open({
      size: 'xl',
      modalId: 'uploadEvidenceModal',
      title: `${t('OrdersPage:evidenceModal.title')} - ${buildOrderFolio(order?.folioPrefix, order?.sequence)}`,
          children: (
        <>
          <UploadOrderEvidenceFile
            onSubmit={async (_values: any, formData: any) => {
              return (await onCreateOrderEvidence(order._id || '', formData)) || false;
            }}
          />
        </>
      )
    });
  };

  const onOrderCleaning = async ({ sequence, storeId }: { sequence: string; storeId: string }) => {
    try {
      if (setOrderCleaning.isPending) return;
      await setOrderCleaning.mutateAsync({
        sequence: sequence,
        storeId: storeId || ''
      });
      showNotification({ type: 'Default', title: t('Notifications:success') });
    } catch (e) {
      showNotification({ type: 'Error', title: t('Notifications:somethingWrong') });
      throw e;
    }
  };

  const onOrderCancelled = async ({ sequence, storeId }: { sequence: string; storeId: string }) => {
    try {
      if (setOrderCancelled.isPending) return;
      await setOrderCancelled.mutateAsync({
        sequence: sequence,
        storeId: storeId,
        data: {
          cancelledDateTime: new Date().toISOString()
        }
      });
      showNotification({ type: 'Default', title: t('Notifications:success') });
    } catch (e) {
      showNotification({ type: 'Error', title: t('Notifications:somethingWrong') });
      throw e;
    }
  };

  const onOrderDelivered = async ({
    orderId,
    sequence,
    storeId
  }: {
    orderId?: string;
    sequence: string;
    storeId: string;
  }) => {
    if (delivering.current || setOrderDelivered.isPending) return;
    const operation = new AbortController();
    delivering.current = operation;
    try {
      const sourceStore = storeId === store?._id ? store : await getStoreByIdV2(storeId) as unknown as IStore;
      if (!sourceStore?._id || sourceStore._id !== storeId) throw new Error(t('Notifications:somethingWrong'));
      const pinUser = sourceStore.requireCashierPinToCollect ? await requestPin({ purpose: 'order-collection', storeId, signal: operation.signal }) : null;
      if (operation.signal.aborted || (sourceStore.requireCashierPinToCollect && !pinUser?._id)) return;
      await setOrderDelivered.mutateAsync({
        sequence: sequence,
        storeId: storeId || '',
        data: {
          collectedDateTime: new Date().toISOString(),
          collectWithAmountDue: true,
          ...(pinUser?._id ? { pinUserId: pinUser._id } : {}),
          ...(pinUser?.pinValidationToken ? { pinValidationToken: pinUser.pinValidationToken } : {})
        }
      });
      showNotification({ type: 'Default', title: t('Notifications:success') });
      void completionTicketAutoPrint(
        orderId || { sequence, storeId },
        sourceStore.orderPageConfig?.printTicketAfterOrderCompletion
      );
    } catch (e) {
      showNotification({ type: 'Error', title: t('Notifications:somethingWrong') });
      throw e;
    } finally {
      if (delivering.current === operation) delivering.current = null;
    }
  };

  const onUpdateOrdersModal = async () => {
    const updateOptions = [
      {
        id: 'markAsCleaned',
        label: t('UpdateOrderForm:updateOrderOptions.markAsCleaning'),
        onSubmit: onOrderCleaning
      },
      {
        id: 'masAsDelivered',
        label: t('UpdateOrderForm:updateOrderOptions.masAsDelivered'),
        onSubmit: onOrderDelivered
      },
      {
        id: 'masAsCancelled',
        label: t('UpdateOrderForm:updateOrderOptions.masAsCancelled'),
        onSubmit: onOrderCancelled
      }
    ];
    modals.open({
      modalId: 'updateOrderModal',
      title: t('UpdateOrderForm:title'),
      children: (
        <>
          <UpdateOrderForm
            updateOptions={updateOptions}
            close={() => modals.close('updateOrderModal')}
            storeId={searchParamStoreId}
          />
        </>
      )
    });
  };

  useEffect(() => {
    return () => {
      queryClient.removeQueries({
        queryKey: ['orders']
      });
    };
  }, []);

  useEffect(() => {
    if (data && selectedOrder) {
      const updatedOrder = (orders || []).find((order: IOrder) => order._id === selectedOrder._id);
      if (updatedOrder) {
        setSelectedOrder(updatedOrder);
        paymentLinesModal.syncOrder(updatedOrder);
      }
    }
  }, [data, selectedOrder?._id]);

  return (
    <>
      <OrderEvidencesCarouselModal
        title={`${t('OrdersPage:evidenceModal.title')}: ${buildOrderFolio(selectedOrder?.folioPrefix, selectedOrder?.sequence)}`}
        close={() => {
          evidenceModalhandlers.close();
        }}
        opened={openedOrderEvidenceModal}
        order={selectedOrder}
        onEvidenceDeleted={handleEvidenceDeleted}
      />
      <OrderPaymentLinesModal
        onAddPaymentLine={onAddPaymentLine}
        title={paymentLinesModal.title}
        close={paymentLinesModal.close}
        opened={paymentLinesModal.opened}
        order={paymentLinesModal.order}
        onDeletePaymentLine={onDeletePaymentLine}
        onUpdatePaymentLine={onUpdatePaymentLine}
        pinUser={paymentLinesModal.pinUser}
      />
      <OrderProductsModal
        title={`${t('OrdersPage:productsModal.title')}: ${buildOrderFolio(selectedOrder?.folioPrefix, selectedOrder?.sequence)}`}
        close={() => {
          orderProductsModalhandlers.close();
        }}
        opened={openedOrderProductsModal}
        order={selectedOrder}
      />
      <CatalogContainer pos="relative">
        <CatalogListHeader title={t('PickupAndDeliveryPage:title')} showCreateButton={false} />
        <LoaderOverlay
          visible={
            isLoading ||
            setOrderCleaning.isPending ||
            setOrderDelivered.isPending ||
            setOrderCancelled.isPending
          }
        />
        <Grid>
          <Grid.Col span={12} my={10}>
            <Grid align="center" justify="center">
              <Grid.Col span={{ xs: 12, md: 8 }}>
                <TextInput
                  ref={searchTermInputRef}
                  onChange={(event) => setSearchParamsDebounced(event.currentTarget.value)}
                  placeholder={`${t('FieldName:order')}, ${t('FieldName:customer')}, ${t(
                    'FieldName:phone'
                  )}`}
                  label={`${t('FieldName:order')}`}
                />
              </Grid.Col>
              <Grid.Col span={{ xs: 12, md: 2 }} style={{ alignSelf: 'flex-end' }}>
                <Button fullWidth type="button" color="gray" onClick={clearSearchTerm}>
                  {t('CommonButtons:clear').toUpperCase()}
                </Button>
              </Grid.Col>
              <Grid.Col span={{ xs: 12, md: 2 }} style={{ alignSelf: 'flex-end' }}>
                <Flex justify="space-around">
                  <Tooltip withinPortal={false} label={t('CommonButtons:exportList')}>
                    <ActionIcon
                      disabled={loadingExport}
                      onClick={() => {
                        downloadExcelV2(async () => {
                          try {
                            return await exportOrdersListV2({
                              q: searchTerm,
                              status: orderStatusFilter,
                              storeId: searchParamStoreId
                            });
                          } catch (e) {
                            throw e;
                          }
                        }, `Ordenes_${searchParamStoreId}`);
                      }}
                      variant="filled"
                      color="#1D6F42"
                      aria-label="Descargar_Listado_CSV_Ordenes">
                      <IconFileSpreadsheet style={{ width: '70%', height: '70%' }} stroke={1.5} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip withinPortal={false} label="Listado en tarjetas">
                    <ActionIcon
                      disabled={loadingExport}
                      onClick={() => {
                        setShowTableView(false);
                      }}
                      variant="filled"
                      color="black"
                      aria-label="Descargar_Listado_CSV_Ordenes">
                      <IconLayoutGrid style={{ width: '70%', height: '70%' }} stroke={1.5} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip withinPortal={false} label="Listado en tabla">
                    <ActionIcon
                      disabled={loadingExport}
                      onClick={() => {
                        setShowTableView(true);
                      }}
                      variant="filled"
                      color="black"
                      aria-label="Descargar_Listado_CSV_Ordenes">
                      <IconColumns style={{ width: '70%', height: '70%' }} stroke={1.5} />
                    </ActionIcon>
                  </Tooltip>
                </Flex>
              </Grid.Col>
            </Grid>
          </Grid.Col>
        </Grid>

        <Grid
          pos={'sticky'}
          top={'calc(var(--app-shell-header-offset, 0rem) + var(--app-shell-padding))'}
          justify="center"
          align="center"
          style={{
            zIndex: 100
          }}>
          <Grid.Col span={12} pt={0}>
            <Card mt={0}>
              <Grid gutter={'md'}>
                <Grid.Col
                  span={{
                    xs: 12,
                    md: company?.companyServicesPermissions?.canUseDeliveryApp ? 4 : 2
                  }}>
                  <Grid>
                    <Grid.Col
                      span={company?.companyServicesPermissions?.canUseDeliveryApp ? 6 : 12}>
                      <Button fullWidth onClick={onUpdateOrdersModal} type="button">
                        Actualizar Pedido
                      </Button>
                    </Grid.Col>
                    {company?.companyServicesPermissions?.canUseDeliveryApp && (
                      <Grid.Col span={6}>
                        <Button
                          fullWidth
                          onClick={() => {
                            history.push(`/routes?storeId=${searchParamStoreId}`);
                          }}>
                          Rutas a Domicilio
                        </Button>
                      </Grid.Col>
                    )}
                  </Grid>
                </Grid.Col>
                <Grid.Col
                  span={{
                    xs: 12,
                    md: company?.companyServicesPermissions?.canUseDeliveryApp ? 8 : 10
                  }}>
                  <Grid justify="left" align="center">
                    <Grid.Col span={3}>
                      <Text fw={'bold'}>
                        {t('OrdersHeaderSummary:orders')}: {summaryData?.ordersTotal}
                      </Text>
                    </Grid.Col>
                    <Grid.Col span={3}>
                      <Text fw={'bold'}>
                        {t('OrdersHeaderSummary:pieces')}:{' '}
                        {(summaryData?.totalPieces || 0).toFixed(2)}
                      </Text>
                    </Grid.Col>
                    {!hideOrderTotals && (
                      <>
                        <Grid.Col span={3}>
                          <Text fw={'bold'}>
                            {t('OrdersHeaderSummary:total')}:{' '}
                            {formatMoneyString((summaryData?.total || 0).toFixed(2))}
                          </Text>
                        </Grid.Col>
                        <Grid.Col span={3}>
                          <Text fw={'bold'}>
                            {t('OrdersHeaderSummary:amountDue')}:{' '}
                            {formatMoneyString((summaryData?.unpaidTotal || 0).toFixed(2))}
                          </Text>
                        </Grid.Col>
                      </>
                    )}
                  </Grid>
                </Grid.Col>
              </Grid>
            </Card>
          </Grid.Col>
        </Grid>

        {showTableView && (
          <OrdersTableView
            orders={orders}
            totalRowsCount={totalRowsCount}
            pages={pages}
            isError={isError}
            isLoading={isLoading}
            pagination={pagination}
            setPagination={setPagination}
            searchParamStoreId={searchParamStoreId}
            storeReceptionValidationEnabled={storeReceptionValidationEnabled}
            onOrderDelivered={onOrderDelivered}
            onOrderCleaning={onOrderCleaning}
            onOrderCancelled={onOrderCancelled}
            showPaymentLinesModal={showPaymentLinesModal}
            showOrderProductsModal={showOrderProductsModal}
          />
        )}
        {!showTableView && (
          <OrdersCardView
            orders={orders}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            searchParamStoreId={searchParamStoreId}
            storeReceptionValidationEnabled={storeReceptionValidationEnabled}
            onOrderDelivered={onOrderDelivered}
            onOrderCleaning={onOrderCleaning}
            onOrderCancelled={onOrderCancelled}
            onSendEmailReceipt={onSendEmailReceipt}
            onPrintTicket={onPrintTicket}
            onDownloadPNGTicket={onDownloadPNGTicket}
            showPaymentLinesModal={showPaymentLinesModal}
            showOrderProductsModal={showOrderProductsModal}
            onShowCreateOrderEvidenceModal={onShowCreateOrderEvidenceModal}
            onShowOrderEvidenceModal={showOrderEvidenceModal}
          />
        )}
      </CatalogContainer>
    </>
  );
};

export default PickupAndDeliveryOrders;
